/**
 * Manual / platform draw publish normalization.
 *
 * Official FL/NY reading is Pick 3 · Pick 4 (`###.####`). Settlement still uses
 * the derived bolita triple (last 2 of Pick 3 · first 2 of Pick 4 · last 2 of Pick 4).
 */

export interface NormalizedManualDraw {
  first: string;
  second: string;
  third: string;
  /** Full Pick-3 (3 digits) when known. */
  pick3?: string;
  /** Full Pick-4 / Win 4 (4 digits) — always second+third when from bolita fields. */
  pick4?: string;
}

function digitsOnly(raw: unknown): string {
  return String(raw ?? '').replace(/\D/g, '');
}

/** Derive bolita triple + official picks from Pick 3 (3) and Pick 4 (4). */
export function normalizeOfficialPicksInput(
  pick3Raw: string,
  pick4Raw: string,
): NormalizedManualDraw | null {
  const pick3 = digitsOnly(pick3Raw);
  const pick4 = digitsOnly(pick4Raw);
  if (!/^\d{3}$/.test(pick3) || !/^\d{4}$/.test(pick4)) return null;
  return {
    first: pick3.slice(-2),
    second: pick4.slice(0, 2),
    third: pick4.slice(2, 4),
    pick3,
    pick4,
  };
}

/**
 * Banker-style triple entry: 1st may be 2 (fijo) or 3 (Pick 3).
 * Always persists Pick 4 as second+third when valid.
 */
export function normalizeManualDrawInput(
  firstRaw: string,
  secondRaw: string,
  thirdRaw: string,
): NormalizedManualDraw | null {
  const firstDigits = digitsOnly(firstRaw);
  const second = digitsOnly(secondRaw);
  const third = digitsOnly(thirdRaw);
  if (!/^\d{2}$/.test(second) || !/^\d{2}$/.test(third)) return null;
  const pick4 = `${second}${third}`;
  if (/^\d{3}$/.test(firstDigits)) {
    return {
      first: firstDigits.slice(-2),
      second,
      third,
      pick3: firstDigits,
      pick4,
    };
  }
  if (/^\d{2}$/.test(firstDigits)) {
    return { first: firstDigits, second, third, pick4 };
  }
  return null;
}
